/**
 * DriveView — Google Drive-style folder browser для топика kind='drive'.
 *
 * Рендерится в ChatPage вместо messages-feed когда activeTopic.kind === 'drive'.
 * Использует /api/chat/drive/* endpoints. Реюзит chat-attachments URL для
 * preview/thumbnail.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Icon } from "@/components/prototype/Icon";
import {
  fetchDriveList, createDriveFolder, deleteDriveFolder, uploadDriveFile,
  deleteDriveFile, patchDriveFolder, patchDriveFile, searchDrive,
  fetchDriveTrash, restoreDriveFile,
  fetchDriveUploaders,
  moveFolderToDrive,
  type DriveFile,
  type DriveSearchFolderHit, type DriveSearchFileHit,
} from "@/api/drive";
import { fetchChatTopics } from "@/api/dashboard";
import { Avatar } from "@/components/prototype/Avatar";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { VideoLightbox } from "@/components/VideoLightbox";
import { FilePreviewModal } from "@/components/FilePreviewModal";

interface Props {
  topicId: number;
  topicName: string;
  /** Опц. начальная папка — для deep-link из карточки производства
      `?folder=N`. Применяется один раз при монтировании. */
  initialFolderId?: number | null;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} КБ`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} МБ`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} ГБ`;
}

function fileKind(f: DriveFile): "image" | "video" | "audio" | "pdf" | "doc" | "other" {
  const m = (f.mime || "").toLowerCase();
  if (m.startsWith("image/")) return "image";
  if (m.startsWith("video/")) return "video";
  if (m.startsWith("audio/")) return "audio";
  if (m.includes("pdf")) return "pdf";
  if (m.includes("word") || m.includes("document") || m.includes("sheet") || m.includes("excel")) return "doc";
  return "other";
}

function fileIcon(kind: string): string {
  switch (kind) {
    case "image": return "image";
    case "video": return "film";
    case "audio": return "mic";
    case "pdf": return "file";
    case "doc": return "file";
    default: return "file";
  }
}

const DATE_PRESET_LABELS: Record<string, string> = {
  any: "За всё время",
  today: "Сегодня",
  yesterday: "Вчера",
  week: "За неделю",
  month: "За месяц",
};

function useMemoDateFrom(preset: string): string {
  return useMemo(() => {
    if (preset === "any") return "";
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    if (preset === "yesterday") d.setDate(d.getDate() - 1);
    else if (preset === "week") d.setDate(d.getDate() - 7);
    else if (preset === "month") d.setDate(d.getDate() - 30);
    // today → начало сегодняшнего дня (уже стоит)
    return d.toISOString().slice(0, 19).replace("T", " ");
  }, [preset]);
}

export function DriveView({ topicId, topicName, initialFolderId }: Props) {
  const qc = useQueryClient();
  // Initial folder: prop (от ChatPage) ИЛИ прямое чтение ?folder=N из URL
  // как fallback на случай, если ChatPage не успел передать prop корректно
  // (TanStack search хук иногда отдаёт пустой объект на первом mount, см.
  // баг 2026-06-05: открытие из /production карточки приводило в root диска).
  const folderFromUrl = (() => {
    if (typeof window === "undefined") return null;
    const u = new URLSearchParams(window.location.search);
    const f = u.get("folder");
    const t = u.get("topic");
    if (!f || !t) return null;
    if (Number(t) !== topicId) return null;
    const n = Number(f);
    return Number.isFinite(n) ? n : null;
  })();
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(
    initialFolderId ?? folderFromUrl ?? null,
  );
  const [newFolderModalOpen, setNewFolderModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [trashOpen, setTrashOpen] = useState(false);
  // «Перенести на другой Диск» — выбранная папка-источник
  const [moveFolderTarget, setMoveFolderTarget] = useState<{ id: number; name: string } | null>(null);
  const [ctxItem, setCtxItem] = useState<{
    kind: "folder" | "file";
    id: number;
    name: string;
    x: number;
    y: number;
    /** Если задано — file лежит в этой папке; показываем «Перейти к папке». */
    parentFolder?: { id: number; label: string };
  } | null>(null);
  const [renameDialog, setRenameDialog] = useState<{ kind: "folder" | "file"; id: number; name: string } | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [lightboxFile, setLightboxFile] = useState<DriveFile | null>(null);
  const [previewFile, setPreviewFile] = useState<DriveFile | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [searchQ, setSearchQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [datePreset, setDatePreset] = useState<"any" | "today" | "yesterday" | "week" | "month">("any");
  const [uploaderFilter, setUploaderFilter] = useState<string>("");
  const [filtersOpen, setFiltersOpen] = useState<"date" | "author" | null>(null);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [syncingAmo, setSyncingAmo] = useState(false);

  // Кнопка «Обновить из Amo» — видна только на «Диск (Продажи)» (id=8428).
  // Запускает /api/chat/drive/sync-amo-leads, который пересоздаёт/обновляет
  // папки сделок (Выезды + Договора) исходя из текущего state в AmoCRM.
  const SALES_DRIVE_ID = 8428;
  const showAmoSyncBtn = topicId === SALES_DRIVE_ID && currentFolderId === null;

  const refreshFromAmo = useCallback(async () => {
    if (syncingAmo) return;
    if (!window.confirm("Запустить обновление папок сделок из AmoCRM? Это займёт ~10-30 секунд.")) return;
    setSyncingAmo(true);
    try {
      const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
      const res = await fetch(`${base}/chat/drive/sync-amo-leads`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
      const data = await res.json();
      qc.invalidateQueries({ queryKey: ["drive/list", topicId] });
      const lines = [
        `Обработано сделок Amo: ${data.leads_processed} из ${data.total_amo_leads}`,
        `Новых папок создано: ${data.folders_created}`,
        `Переездов между стадиями: ${data.stage_moves}`,
        data.errors > 0 ? `Ошибок: ${data.errors}` : null,
      ].filter(Boolean).join("\n");
      alert("Готово!\n\n" + lines);
    } catch (e) {
      alert(`Не удалось: ${(e as Error).message}`);
    } finally {
      setSyncingAmo(false);
    }
  }, [syncingAmo, qc, topicId]);

  // Создание нового документа Word — кладём в текущую папку Диска и открываем в SuperDoc.
  // Таблицы/презентации через OnlyOffice больше не создаём.
  const createDocument = useCallback(async (kind: "word" | "cell" | "slide") => {
    if (creating) return;
    setCreating(true);
    const isCell = kind === "cell";
    const name = window.prompt(isCell ? "Имя новой таблицы Excel" : "Имя нового документа Word", isCell ? "Таблица" : "Документ");
    if (!name) { setCreating(false); return; }
    try {
      const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
      const body: Record<string, unknown> = { name, kind: isCell ? "cell" : "word", drive_topic_id: topicId };
      if (currentFolderId != null) body.folder_id = currentFolderId;
      const res = await fetch(`${base}/pages/word/create`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      qc.invalidateQueries({ queryKey: ["drive/list", topicId, currentFolderId] });
      window.location.assign(isCell ? `/pages/sheet/${data.attachment_id}` : `/pages/word/${data.attachment_id}`);
    } catch (e) {
      alert(`Не удалось создать: ${(e as Error).message}`);
    } finally {
      setCreating(false);
      setCreateMenuOpen(false);
    }
  }, [creating, topicId, currentFolderId, qc]);

  // Закрыть create-меню при клике вне
  useEffect(() => {
    if (!createMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest(".drive-create-wrap")) return;
      setCreateMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [createMenuOpen]);

  // Сбрасываем при СМЕНЕ топика (не на первом mount — иначе перетирает
  // initialFolderId из deep-link `?folder=N` из карточки производства).
  const prevTopicIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (prevTopicIdRef.current === null) {
      prevTopicIdRef.current = topicId;
      return;
    }
    if (prevTopicIdRef.current === topicId) return;
    prevTopicIdRef.current = topicId;
    setCurrentFolderId(null);
    setSearchQ("");
    setDebouncedQ("");
    setDatePreset("any");
    setUploaderFilter("");
    setFiltersOpen(null);
  }, [topicId]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchQ.trim()), 200);
    return () => clearTimeout(t);
  }, [searchQ]);

  // Закрыть filter popover по клику вне
  useEffect(() => {
    if (!filtersOpen) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest(".drive-filter-wrap")) return;
      setFiltersOpen(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [filtersOpen]);

  const dateFromIso = useMemoDateFrom(datePreset);
  const searchMode = debouncedQ.length >= 1 || datePreset !== "any" || !!uploaderFilter;

  const listQ = useQuery({
    queryKey: ["drive/list", topicId, currentFolderId],
    queryFn: () => fetchDriveList(topicId, currentFolderId),
    staleTime: 30_000,
    enabled: !searchMode,
  });

  const searchQuery = useQuery({
    queryKey: ["drive/search", topicId, debouncedQ, dateFromIso, uploaderFilter],
    queryFn: () => searchDrive(topicId, {
      q: debouncedQ || undefined,
      date_from: dateFromIso || undefined,
      uploaded_by: uploaderFilter || undefined,
    }),
    staleTime: 30_000,
    enabled: searchMode,
  });

  const uploadersQ = useQuery({
    queryKey: ["drive/uploaders", topicId],
    queryFn: () => fetchDriveUploaders(topicId),
    staleTime: 5 * 60_000,
  });

  const refetch = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["drive/list", topicId, currentFolderId] });
  }, [qc, topicId, currentFolderId]);

  const createFolderMut = useMutation({
    mutationFn: () => createDriveFolder(topicId, newFolderName.trim(), currentFolderId),
    onSuccess: () => {
      setNewFolderModalOpen(false);
      setNewFolderName("");
      refetch();
    },
    onError: (e: Error) => alert(`Не удалось создать папку: ${e.message}`),
  });

  const deleteFolderMut = useMutation({
    mutationFn: (folderId: number) => deleteDriveFolder(folderId),
    onSuccess: refetch,
    onError: (e: Error) => alert(`Не удалось удалить: ${e.message}`),
  });

  const deleteFileMut = useMutation({
    mutationFn: (fileId: number) => deleteDriveFile(fileId),
    onSuccess: refetch,
    onError: (e: Error) => alert(`Не удалось удалить: ${e.message}`),
  });

  // «Убрать из папки» — не удаление, а перенос в корень диска (folder_id=null).
  // Сам drive_files row остаётся, deleted_at=NULL.
  const unfileMut = useMutation({
    mutationFn: (fileId: number) => patchDriveFile(fileId, { folder_id: null }),
    onSuccess: refetch,
    onError: (e: Error) => alert(`Не удалось убрать: ${e.message}`),
  });

  const renameMut = useMutation({
    mutationFn: async () => {
      if (!renameDialog) return;
      const name = renameValue.trim();
      if (!name) throw new Error("Имя не может быть пустым");
      if (renameDialog.kind === "folder") {
        await patchDriveFolder(renameDialog.id, { name });
      } else {
        await patchDriveFile(renameDialog.id, { name });
      }
    },
    onSuccess: () => {
      setRenameDialog(null);
      setRenameValue("");
      refetch();
    },
    onError: (e: Error) => alert(`Не удалось переименовать: ${e.message}`),
  });

  // Загрузка файлов (drag-drop + кнопка)
  const uploadFiles = useCallback(async (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setUploadingCount(arr.length);
    try {
      for (const f of arr) {
        await uploadDriveFile(topicId, f, currentFolderId);
      }
      refetch();
    } catch (e) {
      alert(`Загрузка не удалась: ${(e as Error).message}`);
    } finally {
      setUploadingCount(0);
    }
  }, [topicId, currentFolderId, refetch]);

  function onFilePick(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) uploadFiles(e.target.files);
    e.target.value = "";
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) uploadFiles(e.dataTransfer.files);
  }

  function openFile(f: DriveFile) {
    const kind = fileKind(f);
    if (kind === "image" || kind === "video") {
      setLightboxFile(f);
    } else {
      // PDF/DOCX/XLSX/PPTX/TXT → встроенный preview, остальное → скачать
      setPreviewFile(f);
    }
  }

  function onCtxClose() { setCtxItem(null); }
  useEffect(() => {
    if (!ctxItem) return;
    const close = () => setCtxItem(null);
    window.addEventListener("click", close);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("click", close); window.removeEventListener("keydown", onKey); };
  }, [ctxItem]);

  const data = listQ.data;
  const isEmpty = data && data.folders.length === 0 && data.files.length === 0;

  return (
    <div
      className="drive-view"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      {/* Header — две строки: 1) название/путь, 2) поиск + кнопки */}
      <div className="drive-header drive-header-row1">
        {currentFolderId != null && (
          <button
            type="button"
            className="drive-back-btn"
            title="Назад"
            aria-label="Назад"
            onClick={() => {
              // Поднимаемся на 1 уровень вверх по breadcrumbs.
              // breadcrumbs от root до текущей включительно — берём предпоследнюю.
              const crumbs = data?.breadcrumbs ?? [];
              if (crumbs.length <= 1) setCurrentFolderId(null);
              else setCurrentFolderId(crumbs[crumbs.length - 2].id);
            }}
          >
            <Icon name="chevron_l" size={18}/>
          </button>
        )}
        <div className="drive-breadcrumbs">
          <button
            type="button"
            className="drive-crumb drive-crumb-root"
            onClick={() => setCurrentFolderId(null)}
            title={topicName}
          >
            <Icon name="folder" size={16}/>
            <span>{topicName}</span>
          </button>
          {data?.breadcrumbs.map((c, idx) => {
            const isLast = idx === (data?.breadcrumbs.length ?? 0) - 1;
            return (
              <span key={c.id} style={{ display: "contents" }}>
                <span className="drive-crumb-sep">/</span>
                <button
                  type="button"
                  className={`drive-crumb${isLast ? " drive-crumb-current" : ""}`}
                  onClick={() => setCurrentFolderId(c.id)}
                  title={c.name}
                >
                  {c.name}
                </button>
              </span>
            );
          })}
        </div>
      </div>
      <div className="drive-header drive-header-row2">
        <div className="drive-actions">
          <div className="drive-search-wrap">
            <Icon name="search" size={14}/>
            <input
              type="search"
              className="drive-search"
              placeholder="Поиск по папкам и файлам…"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
            />
            {searchQ && (
              <button
                type="button"
                className="drive-search-clear"
                onClick={() => setSearchQ("")}
                aria-label="Очистить"
              >
                <Icon name="x" size={12}/>
              </button>
            )}
          </div>

          {/* Date preset chip */}
          <div className="drive-filter-wrap">
            <button
              type="button"
              className={`drive-filter-chip${datePreset !== "any" ? " is-active" : ""}${datePreset === "any" ? " drive-filter-chip-icon" : ""}`}
              onClick={() => setFiltersOpen(filtersOpen === "date" ? null : "date")}
              title="Дата"
              aria-label="Фильтр по дате"
            >
              <Icon name="clock" size={14}/>
              {datePreset !== "any" && (
                <>
                  <span>{DATE_PRESET_LABELS[datePreset]}</span>
                  <span
                    className="drive-filter-chip-x"
                    onClick={(e) => { e.stopPropagation(); setDatePreset("any"); setFiltersOpen(null); }}
                    role="button"
                  ><Icon name="x" size={10}/></span>
                </>
              )}
            </button>
            {filtersOpen === "date" && (
              <div className="drive-filter-pop">
                {(["any","today","yesterday","week","month"] as const).map(p => (
                  <button
                    key={p}
                    type="button"
                    className={`drive-filter-pop-item${p === datePreset ? " is-active" : ""}`}
                    onClick={() => { setDatePreset(p); setFiltersOpen(null); }}
                  >
                    {DATE_PRESET_LABELS[p]}
                    {p === datePreset && <Icon name="check" size={14}/>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Author chip */}
          <div className="drive-filter-wrap">
            <button
              type="button"
              className={`drive-filter-chip${uploaderFilter ? " is-active" : ""}${!uploaderFilter ? " drive-filter-chip-icon" : ""}`}
              onClick={() => setFiltersOpen(filtersOpen === "author" ? null : "author")}
              title="Автор"
              aria-label="Фильтр по автору"
            >
              <Icon name="users" size={14}/>
              {uploaderFilter && (
                <>
                  <span>{uploadersQ.data?.find(u => u.id === uploaderFilter)?.display_name ?? "Автор"}</span>
                  <span
                    className="drive-filter-chip-x"
                    onClick={(e) => { e.stopPropagation(); setUploaderFilter(""); setFiltersOpen(null); }}
                    role="button"
                  ><Icon name="x" size={10}/></span>
                </>
              )}
            </button>
            {filtersOpen === "author" && (
              <div className="drive-filter-pop drive-filter-pop-authors">
                {(uploadersQ.data ?? []).length === 0 && (
                  <div className="drive-filter-pop-empty">Никто ещё не загружал файлы</div>
                )}
                {(uploadersQ.data ?? []).map(u => (
                  <button
                    key={u.id}
                    type="button"
                    className={`drive-filter-pop-item${u.id === uploaderFilter ? " is-active" : ""}`}
                    onClick={() => { setUploaderFilter(u.id); setFiltersOpen(null); }}
                  >
                    <Avatar
                      name={u.display_name}
                      userId={u.id}
                      avatarFilename={u.avatar_filename ?? null}
                      size={20}
                    />
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.display_name}</span>
                    <span className="drive-filter-pop-count">{u.files_count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className="btn btn-sm drive-toolbar-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingCount > 0}
            title="Загрузить файл"
            aria-label="Загрузить файл"
          >
            <Icon name="paperclip" size={14}/>
            <span className="drive-toolbar-btn-label">{uploadingCount > 0 ? `Загрузка ${uploadingCount}…` : "Загрузить"}</span>
          </button>
          {showAmoSyncBtn && (
            <button
              type="button"
              className="btn btn-sm btn-secondary drive-toolbar-btn"
              onClick={refreshFromAmo}
              disabled={syncingAmo}
              title="Обновить структуру папок сделок из AmoCRM (Выезды + Договора)"
              aria-label="Обновить из Amo"
            >
              <Icon name="refresh" size={14}/>
              <span className="drive-toolbar-btn-label">{syncingAmo ? "Обновление…" : "Обновить из Amo"}</span>
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm btn-secondary drive-toolbar-btn"
            onClick={() => setTrashOpen(true)}
            title="Корзина (удалённые за последние 30 дней)"
            aria-label="Корзина"
          >
            <Icon name="trash" size={14}/>
            <span className="drive-toolbar-btn-label">Корзина</span>
          </button>
          <div className="drive-create-wrap" style={{ position: "relative" }}>
            <button
              type="button"
              className="btn btn-sm btn-primary drive-toolbar-btn drive-create-btn"
              onClick={() => setCreateMenuOpen(v => !v)}
              disabled={creating}
              title="Создать"
              aria-label="Создать"
            >
              <Icon name="plus" size={14}/>
              <span className="drive-toolbar-btn-label">{creating ? "Создаю…" : "Создать"}</span>
            </button>
            {createMenuOpen && (
              <div className="drive-create-menu">
                <button
                  type="button"
                  onClick={() => {
                    setCreateMenuOpen(false);
                    setNewFolderName("");
                    setNewFolderModalOpen(true);
                  }}
                >
                  <span className="drive-create-icon"><Icon name="folder" size={18}/></span>
                  <span><b>Новая папка</b><small>добавить в текущий раздел</small></span>
                </button>
                <div className="drive-create-divider" role="separator"/>
                <button type="button" onClick={() => createDocument("word")}>
                  <span className="drive-create-icon"><Icon name="report" size={18}/></span>
                  <span><b>Документ Word</b><small>.docx — откроется в редакторе</small></span>
                </button>
                <button type="button" onClick={() => createDocument("cell")}>
                  <span className="drive-create-icon"><Icon name="chart" size={18}/></span>
                  <span><b>Таблица Excel</b><small>.xlsx — откроется в редакторе</small></span>
                </button>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={onFilePick}
          />
        </div>
      </div>

      {/* Content */}
      <div className="drive-content">
        {!searchMode && listQ.isLoading && <div className="drive-loading">Загрузка…</div>}
        {!searchMode && listQ.error && (
          <div className="drive-error" role="alert">
            <div>Не удалось загрузить Диск: {(listQ.error as Error).message}</div>
            <button type="button" className="btn btn-sm btn-primary" onClick={() => void listQ.refetch()}>
              <Icon name="refresh" size={14}/> Повторить
            </button>
          </div>
        )}
        {!searchMode && isEmpty && (
          <div className="drive-empty">
            <Icon name="folder" size={48}/>
            <div className="drive-empty-title">Пусто</div>
            <div className="drive-empty-sub">Загрузите файл или создайте папку. Можно перетащить файлы прямо сюда.</div>
          </div>
        )}

        {searchMode && (() => {
          const sd = searchQuery.data;
          const total = (sd?.folders.length ?? 0) + (sd?.files.length ?? 0);
          return (
            <div className="drive-search-results">
              <div className="drive-search-header">
                <span>
                  {debouncedQ ? `Поиск: «${debouncedQ}»` : "Фильтр"}
                  {datePreset !== "any" && <span className="drive-search-flag">{DATE_PRESET_LABELS[datePreset]}</span>}
                  {uploaderFilter && (
                    <span className="drive-search-flag">
                      {uploadersQ.data?.find(u => u.id === uploaderFilter)?.display_name ?? "автор"}
                    </span>
                  )}
                </span>
                {!searchQuery.isLoading && <span className="drive-search-count">{total} {pluralRu(total, ["результат", "результата", "результатов"])}</span>}
              </div>
              {searchQuery.isLoading && <div className="drive-loading">Поиск…</div>}
              {searchQuery.error && (
                <div className="drive-error" role="alert">
                  <div>Не удалось выполнить поиск: {(searchQuery.error as Error).message}</div>
                  <button type="button" className="btn btn-sm btn-primary" onClick={() => void searchQuery.refetch()}>
                    <Icon name="refresh" size={14}/> Повторить
                  </button>
                </div>
              )}
              {!searchQuery.isLoading && !searchQuery.error && total === 0 && (
                <div className="drive-empty">
                  <Icon name="search" size={48}/>
                  <div className="drive-empty-title">Ничего не найдено</div>
                  <div className="drive-empty-sub">Попробуйте другое имя или часть названия.</div>
                </div>
              )}
              {sd && sd.folders.length > 0 && (
                <>
                  <div className="drive-search-group-label">Папки · {sd.folders.length}</div>
                  <div className="drive-search-list">
                    {sd.folders.map((f: DriveSearchFolderHit) => (
                      <button
                        key={`sf-${f.id}`}
                        type="button"
                        className="drive-search-row"
                        onClick={() => { setCurrentFolderId(f.id); setSearchQ(""); }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setCtxItem({ kind: "folder", id: f.id, name: f.name, x: e.clientX, y: e.clientY });
                        }}
                      >
                        <div className="drive-search-row-icon"><Icon name="folder" size={28}/></div>
                        <div className="drive-search-row-text">
                          <div className="drive-search-row-name">{f.name}</div>
                          <div className="drive-search-row-path">
                            {f.path ? `${topicName} › ${f.path}` : topicName}
                            <span className="drive-search-row-meta">
                              · {f.items_count} {pluralRu(f.items_count, ["элемент", "элемента", "элементов"])}
                            </span>
                          </div>
                        </div>
                        <Icon name="chevron_r" size={14}/>
                      </button>
                    ))}
                  </div>
                </>
              )}
              {sd && sd.files.length > 0 && (
                <>
                  <div className="drive-search-group-label">Файлы · {sd.files.length}</div>
                  <div className="drive-search-list">
                    {sd.files.map((file: DriveSearchFileHit) => {
                      const fileAsDriveFile: DriveFile = {
                        id: file.id, topic_id: topicId, folder_id: file.folder_id,
                        attachment_id: file.attachment_id, name: file.name,
                        uploaded_by_user_id: "", uploaded_at: file.uploaded_at,
                        mime: file.mime, size_bytes: file.size_bytes,
                        width: file.width, height: file.height,
                        duration_sec: file.duration_sec, thumb_b64: file.thumb_b64,
                      };
                      const kind = fileKind(fileAsDriveFile);
                      return (
                        <button
                          key={`sfile-${file.id}`}
                          type="button"
                          className="drive-search-row"
                          onClick={() => {
                            openFile(fileAsDriveFile);
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            const lastSegment = file.folder_path
                              ? file.folder_path.split(" › ").pop() || topicName
                              : topicName;
                            setCtxItem({
                              kind: "file",
                              id: file.id,
                              name: file.name,
                              x: e.clientX,
                              y: e.clientY,
                              parentFolder: file.folder_id != null
                                ? { id: file.folder_id, label: lastSegment }
                                : undefined,
                            });
                          }}
                        >
                          <div className="drive-search-row-icon"><Icon name={fileIcon(kind)} size={28}/></div>
                          <div className="drive-search-row-text">
                            <div className="drive-search-row-name">{file.name}</div>
                            <div className="drive-search-row-path">
                              {file.folder_path ? `${topicName} › ${file.folder_path}` : topicName}
                              <span className="drive-search-row-meta">
                                · {fmtBytes(file.size_bytes)}
                              </span>
                            </div>
                          </div>
                          <Icon name="chevron_r" size={14}/>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {!searchMode && data && (
          <div className="drive-grid">
            {data.folders.map((f) => (
              <button
                key={`fld-${f.id}`}
                type="button"
                className="drive-card drive-card-folder"
                onDoubleClick={() => setCurrentFolderId(f.id)}
                onClick={() => setCurrentFolderId(f.id)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setCtxItem({ kind: "folder", id: f.id, name: f.name, x: e.clientX, y: e.clientY });
                }}
                title={f.name}
              >
                <div className="drive-card-icon">
                  <Icon name="folder" size={48}/>
                </div>
                <div className="drive-card-name">{f.name}</div>
                <div className="drive-card-meta">{f.items_count} {pluralRu(f.items_count, ["элемент", "элемента", "элементов"])}</div>
              </button>
            ))}
            {data.files.map((f) => {
              const kind = fileKind(f);
              const isImg = kind === "image";
              const isVid = kind === "video";
              // Универсальное превью (Яндекс.Диск-стиль): backend сам выберет
              // pipeline по типу — image/video/PDF/Office. Для image/video
              // используем raw /preview (быстрее), для остального — /preview-image
              // (рендерит первую страницу через pdftoppm).
              const previewUrl = (isImg || isVid)
                ? `/api/chat/attachments/${f.attachment_id}/preview`
                : `/api/chat/attachments/${f.attachment_id}/preview-image`;
              return (
                <button
                  key={`fil-${f.id}`}
                  type="button"
                  className={`drive-card drive-card-file drive-card-${kind}`}
                  onClick={() => openFile(f)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setCtxItem({ kind: "file", id: f.id, name: f.name, x: e.clientX, y: e.clientY });
                  }}
                  title={`${f.name} · ${fmtBytes(f.size_bytes)}`}
                >
                  <div className="drive-card-icon drive-card-preview">
                    {previewUrl ? (
                      <>
                        <img
                          src={previewUrl}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          style={f.thumb_b64 ? {
                            backgroundImage: `url(data:image/jpeg;base64,${f.thumb_b64})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          } : undefined}
                          onError={(e) => {
                            // 415/404/500 → fallback на иконку. Прячем img,
                            // показываем sibling-fallback.
                            const img = e.currentTarget;
                            img.style.display = "none";
                            const fb = img.nextElementSibling as HTMLElement | null;
                            if (fb) fb.style.display = "flex";
                          }}
                        />
                        <span className="drive-card-icon-fallback" style={{ display: "none" }}>
                          <Icon name={fileIcon(kind)} size={48}/>
                        </span>
                      </>
                    ) : (
                      <Icon name={fileIcon(kind)} size={48}/>
                    )}
                    {isVid && <div className="drive-card-vid-overlay">▶</div>}
                  </div>
                  <div className="drive-card-name">{f.name}</div>
                  <div className="drive-card-meta">{fmtBytes(f.size_bytes)}</div>
                </button>
              );
            })}
          </div>
        )}

        {dragOver && (
          <div className="drive-drop-overlay">
            <Icon name="paperclip" size={48}/>
            <div>Отпустите файл для загрузки</div>
          </div>
        )}
      </div>

      {/* Context menu */}
      {ctxItem && (
        <div
          className="drive-ctx-menu"
          style={{ position: "fixed", left: ctxItem.x, top: ctxItem.y, zIndex: 10000 }}
          onClick={(e) => e.stopPropagation()}
        >
          {ctxItem.parentFolder && (
            <button
              type="button"
              className="drive-ctx-item"
              onClick={() => {
                setCurrentFolderId(ctxItem.parentFolder!.id);
                setSearchQ(""); setDebouncedQ("");
                setDatePreset("any"); setUploaderFilter("");
                setFiltersOpen(null);
                onCtxClose();
              }}
            >
              <Icon name="folder" size={14}/> Перейти в «{ctxItem.parentFolder.label}»
            </button>
          )}
          <button
            type="button"
            className="drive-ctx-item"
            onClick={() => {
              setRenameDialog({ kind: ctxItem.kind, id: ctxItem.id, name: ctxItem.name });
              setRenameValue(ctxItem.name);
              onCtxClose();
            }}
          >
            <Icon name="pencil" size={14}/> Переименовать
          </button>
          {/* «Убрать из папки» — только для файлов, и только когда мы внутри папки.
              Это НЕ удаление: файл остаётся на диске, только folder_id → null. */}
          {ctxItem.kind === "file" && currentFolderId !== null && (
            <button
              type="button"
              className="drive-ctx-item"
              onClick={() => {
                if (confirm(`Убрать «${ctxItem.name}» из этой папки? Файл останется на Диске (в корне).`)) {
                  unfileMut.mutate(ctxItem.id);
                }
                onCtxClose();
              }}
            >
              <Icon name="folder" size={14}/> Убрать из папки
            </button>
          )}
          {/* «Перенести на другой Диск» — только для папок */}
          {ctxItem.kind === "folder" && (
            <button
              type="button"
              className="drive-ctx-item"
              onClick={() => {
                setMoveFolderTarget({ id: ctxItem.id, name: ctxItem.name });
                onCtxClose();
              }}
            >
              <Icon name="folder" size={14}/> Перенести на другой Диск
            </button>
          )}
          <button
            type="button"
            className="drive-ctx-item drive-ctx-item-danger"
            onClick={() => {
              const isFolder = ctxItem.kind === "folder";
              const msg = isFolder
                ? `Удалить папку «${ctxItem.name}»? Папка и её содержимое уйдут в корзину Диска (хранятся 30 дней).`
                : `Удалить «${ctxItem.name}»? Файл уйдёт в корзину Диска и будет храниться 30 дней.`;
              if (confirm(msg)) {
                if (isFolder) deleteFolderMut.mutate(ctxItem.id);
                else deleteFileMut.mutate(ctxItem.id);
              }
              onCtxClose();
            }}
          >
            <Icon name="trash" size={14}/>{" "}
            {ctxItem.kind === "folder" ? "Удалить папку" : "Удалить"}
          </button>
        </div>
      )}

      {/* New folder modal */}
      {newFolderModalOpen && (
        <div className="drive-modal-bg" onClick={() => setNewFolderModalOpen(false)}>
          <div className="drive-modal" onClick={(e) => e.stopPropagation()}>
            <div className="drive-modal-title">Новая папка</div>
            <input
              autoFocus
              className="drive-modal-input"
              type="text"
              placeholder="Название"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newFolderName.trim()) createFolderMut.mutate();
                if (e.key === "Escape") setNewFolderModalOpen(false);
              }}
            />
            <div className="drive-modal-actions">
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => setNewFolderModalOpen(false)}>Отмена</button>
              <button
                type="button"
                className="btn btn-sm"
                disabled={!newFolderName.trim() || createFolderMut.isPending}
                onClick={() => createFolderMut.mutate()}
              >Создать</button>
            </div>
          </div>
        </div>
      )}

      {/* Rename modal */}
      {renameDialog && (
        <div className="drive-modal-bg" onClick={() => setRenameDialog(null)}>
          <div className="drive-modal" onClick={(e) => e.stopPropagation()}>
            <div className="drive-modal-title">Переименовать</div>
            <input
              autoFocus
              className="drive-modal-input"
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && renameValue.trim()) renameMut.mutate();
                if (e.key === "Escape") setRenameDialog(null);
              }}
            />
            <div className="drive-modal-actions">
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => setRenameDialog(null)}>Отмена</button>
              <button
                type="button"
                className="btn btn-sm"
                disabled={!renameValue.trim() || renameMut.isPending}
                onClick={() => renameMut.mutate()}
              >Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox for image/video */}
      {lightboxFile && fileKind(lightboxFile) === "image" && (
        <PhotoLightbox
          images={[{
            src: `/api/chat/attachments/${lightboxFile.attachment_id}/download`,
            previewSrc: `/api/chat/attachments/${lightboxFile.attachment_id}/preview`,
            filename: lightboxFile.name,
          }]}
          idx={0}
          setIdx={() => {}}
          onClose={() => setLightboxFile(null)}
        />
      )}
      {lightboxFile && fileKind(lightboxFile) === "video" && (
        <VideoLightbox
          src={`/api/chat/attachments/${lightboxFile.attachment_id}/download`}
          filename={lightboxFile.name}
          onClose={() => setLightboxFile(null)}
        />
      )}
      {previewFile && (
        <FilePreviewModal
          url={`/api/chat/attachments/${previewFile.attachment_id}/download`}
          filename={previewFile.name}
          mime={previewFile.mime ?? null}
          attachmentId={previewFile.attachment_id}
          onClose={() => setPreviewFile(null)}
          onRenamed={() => {
            qc.invalidateQueries({ queryKey: ["drive/list", topicId, currentFolderId] });
            qc.invalidateQueries({ queryKey: ["drive/search", topicId] });
            qc.invalidateQueries({ queryKey: ["docs/list"] });
            setPreviewFile(null);
          }}
        />
      )}

      {trashOpen && (
        <TrashModal
          topicId={topicId}
          onClose={() => setTrashOpen(false)}
          onRestored={() => {
            qc.invalidateQueries({ queryKey: ["drive/list", topicId, currentFolderId] });
            qc.invalidateQueries({ queryKey: ["drive/search", topicId] });
          }}
        />
      )}

      {moveFolderTarget && (
        <MoveFolderToDriveModal
          folderId={moveFolderTarget.id}
          folderName={moveFolderTarget.name}
          currentTopicId={topicId}
          onClose={() => setMoveFolderTarget(null)}
          onMoved={(targetTopicId) => {
            setMoveFolderTarget(null);
            qc.invalidateQueries({ queryKey: ["drive/list", topicId, currentFolderId] });
            qc.invalidateQueries({ queryKey: ["drive/list", targetTopicId] });
            qc.invalidateQueries({ queryKey: ["chat/topics"] });
          }}
        />
      )}
    </div>
  );
}

function MoveFolderToDriveModal({
  folderId, folderName, currentTopicId, onClose, onMoved,
}: {
  folderId: number;
  folderName: string;
  currentTopicId: number;
  onClose: () => void;
  onMoved: (targetTopicId: number) => void;
}) {
  const topicsQ = useQuery({
    queryKey: ["chat/topics"],
    queryFn: fetchChatTopics,
    staleTime: 60_000,
  });
  const driveTopics = (topicsQ.data ?? []).filter(t => t.kind === "drive" && t.id !== currentTopicId);
  const [query, setQuery] = useState("");
  const filtered = driveTopics.filter(t => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (t.name ?? "").toLowerCase().includes(q);
  });
  const moveMut = useMutation({
    mutationFn: (targetTopicId: number) => moveFolderToDrive(folderId, targetTopicId, null),
    onSuccess: (_data, targetTopicId) => onMoved(targetTopicId),
    onError: (e: Error) => alert(`Не удалось перенести: ${e.message}`),
  });
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-window"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(520px, 92vw)", maxHeight: "80vh", display: "flex", flexDirection: "column" }}
      >
        <div className="modal-header">
          <div style={{ fontFamily: "var(--serif)", fontSize: 18, fontWeight: 700 }}>
            📁 Перенести на другой Диск
          </div>
          <button className="modal-close" onClick={onClose} title="Закрыть">
            <Icon name="x" size={18}/>
          </button>
        </div>
        <div style={{ padding: "8px 18px 6px", fontSize: 12, color: "var(--mute)" }}>
          Папка <b>«{folderName}»</b> со всем содержимым переедет в корень выбранного Диска.
        </div>
        <div style={{ padding: "0 14px 8px" }}>
          <input
            type="search"
            placeholder="Поиск Диска…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            style={{
              width: "100%", padding: "8px 12px",
              border: "1px solid var(--line)", borderRadius: 8,
              background: "var(--bg-1, #f7f7f7)", fontSize: 14, outline: "none",
            }}
          />
        </div>
        <div style={{ overflowY: "auto", flex: 1, padding: "4px 8px 12px" }}>
          {topicsQ.isLoading && <div style={{ padding: 16, color: "var(--mute)" }}>Загрузка…</div>}
          {!topicsQ.isLoading && filtered.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", color: "var(--mute)" }}>
              {query ? "Не найдено" : "Других Дисков нет"}
            </div>
          )}
          {filtered.map(t => (
            <button
              key={t.id}
              type="button"
              disabled={moveMut.isPending}
              onClick={() => {
                if (confirm(`Перенести «${folderName}» на Диск «${t.name}»?`)) {
                  moveMut.mutate(t.id);
                }
              }}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 8,
                border: "1px solid var(--line)", background: "transparent",
                marginBottom: 6, cursor: moveMut.isPending ? "wait" : "pointer",
                display: "flex", alignItems: "center", gap: 10,
                fontFamily: "inherit", fontSize: 14, textAlign: "left",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.04)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <Icon name="folder" size={18}/>
              <span style={{ flex: 1 }}>{t.name}</span>
              <span style={{ color: "var(--mute)", fontSize: 18 }}>›</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function TrashModal({ topicId, onClose, onRestored }: {
  topicId: number;
  onClose: () => void;
  onRestored: () => void;
}) {
  const qc = useQueryClient();
  const trashQ = useQuery({
    queryKey: ["drive/trash", topicId],
    queryFn: () => fetchDriveTrash(topicId),
    staleTime: 0,
  });
  const restoreMut = useMutation({
    mutationFn: (fileId: number) => restoreDriveFile(fileId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["drive/trash", topicId] });
      onRestored();
    },
    onError: (e: Error) => alert(`Не удалось восстановить: ${e.message}`),
  });
  const files = trashQ.data ?? [];
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-window"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(640px, 92vw)", maxHeight: "80vh", display: "flex", flexDirection: "column" }}
      >
        <div className="modal-header">
          <div style={{ fontFamily: "var(--serif)", fontSize: 18, fontWeight: 700 }}>
            🗑 Корзина Диска
          </div>
          <button className="modal-close" onClick={onClose} title="Закрыть">
            <Icon name="x" size={18}/>
          </button>
        </div>
        <div style={{ padding: "8px 16px 6px", fontSize: 12, color: "var(--mute)" }}>
          Удалённые файлы хранятся 30 дней, потом удаляются окончательно.
        </div>
        <div style={{ overflowY: "auto", flex: 1, padding: "4px 8px 12px" }}>
          {trashQ.isLoading && <div style={{ padding: 16, color: "var(--mute)" }}>Загрузка…</div>}
          {!trashQ.isLoading && files.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", color: "var(--mute)" }}>
              Корзина пуста
            </div>
          )}
          {files.map(f => (
            <div
              key={f.id}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "8px 10px", borderRadius: 8,
                borderBottom: "1px solid var(--line)",
              }}
            >
              <Icon name="file" size={20}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {f.name}
                </div>
                <div style={{ fontSize: 11, color: "var(--mute)" }}>
                  {Math.round((f.size_bytes ?? 0) / 1024)} KB
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => restoreMut.mutate(f.id)}
                disabled={restoreMut.isPending}
              >
                Восстановить
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function pluralRu(n: number, forms: [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
}
